/* eslint-disable react/jsx-handler-names */
import { createSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { changeStringField } from "redux/user/slice";
import { userApi } from "redux/user/api";
import AccountPersonalData from "components/molecules/AccountPersonalData";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import { mappedError } from "helpers/errors";
import { useGTM } from "hooks/useGTM";
import * as userSelector from "redux/user/selectors";
import { PhoneCountry } from "types/PhoneCountries";
import { useLocalizationStore } from "stores/localization";
import { useCatalogsStore } from "stores/catalogs";

const AccountPersonalDataContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const GTM = useGTM();
  const state = useSelector(userSelector.all);
  const [errorResponse, setErrorResponse] = useState("");
  const [success, setSuccess] = useState(false);
  const [requestEdit, { isLoading }] = userApi.useEditMutation();
  const { refetch } = userApi.useGetProfileQuery("");
  const methods = useForm({ mode: "onSubmit", shouldFocusError: false });

  const languages = useCatalogsStore((stateLang) => stateLang.languages);
  const language = useLocalizationStore((stateLang) => stateLang.language);

  useEffect(() => {
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  const birthdayDate: string = useWatch({
    control: methods.control,
    name: "birthday",
    defaultValue: methods.getValues("birthday"),
  });

  async function submit(data) {
    const filteredData = Object.fromEntries(
      Object.entries({
        name: data.name,
        birthday: data.birthday,
      }).filter(
        ([_, value]) => value !== undefined && value !== null && value !== "",
      ),
    );

    const languageId =
      (languages?.find((l) => l.isoCode === language) || {}).id || 0;

    const response = await requestEdit({
      ...filteredData,
      phoneNumber: data.phoneNumber,
      languageId,
    });
    // eslint-disable-next-line sonarjs/no-collapsible-if
    if ("data" in response) {
      if (response.data) {
        setSuccess(true);
        refetch();
        setTimeout(() => {
          setSuccess(false);
        }, 1000);
      }
    }
    // eslint-disable-next-line sonarjs/no-collapsible-if
    if ("error" in response) {
      if (response.error) {
        const errors = mappedError(response.error);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < errors.length; i++) {
          if (errors[i].name) {
            methods.setError(errors[i].name, {
              type: "custom",
              message: errors[i].error,
            });
          } else {
            setErrorResponse((prev) => {
              if (prev !== "") {
                return [...prev.split("; "), errors[i].error].join("; ");
              }
              return errors[i].error;
            });
          }
        }
      }
    }
  }

  const handleBlur = (value: string, props) => {
    GTM.inputAccountChange(value);
    dispatch(changeStringField({ field: props.name, value }));
  };

  const onEmailChange = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.changeEmail })}`,
    });
  };

  const onPasswordChange = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.changePassword })}`,
    });
  };

  const onPhoneCountryChange = (value: PhoneCountry) => {
    dispatch(changeStringField({ field: "phoneCountry", value }));
  };

  const onSubmit = (data) => {
    GTM.clickSaveProfile({ ...data, birthday: birthdayDate });
    submit({ ...data, birthday: birthdayDate });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <AccountPersonalData
          data={state}
          errors={methods.formState.errors}
          onEmailChange={onEmailChange}
          onPasswordChange={onPasswordChange}
          onPhoneCountryChange={onPhoneCountryChange}
          handleBlur={handleBlur}
          isLoading={isLoading}
          success={success}
        />
        {errorResponse && <div>{errorResponse}</div>}
      </form>
    </FormProvider>
  );
};

export default AccountPersonalDataContainer;
